import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "reducers";
import { IExemptionSearch } from "../../domain/IExemptionSearch";
import { IGetExemptionsResponse } from "../../domain/IGetExemptionsResponse";
import { consumablesService } from "../../services/consumables.service";
import { IPopoverState } from "Dashboard/scenes/Carnets/domain/IPopoverState";
import { IOutputListItem } from "../../domain/IOutputListItem";
import { IConsumablesOutputUpdate } from "../../domain/IConsumablesOutputUpdate";
import { IExemption } from "../../domain/IExemption";
import { IGetPageRequestParams, IUpdateStateAfterSearchParams } from "hooks/pagingAndSort";

export const getExemptions = createAsyncThunk(
  "Consumables/getExemptions",
  async (params: {
    getOutputListItemsSearchParams: IExemptionSearch,
    pagingParams: IGetPageRequestParams,
    abortSignal: AbortSignal,
    updateStateAfterFetch: (
      updateStateParams: IUpdateStateAfterSearchParams
    ) => void
  }) => {
    try {
      const response = await consumablesService.getExemptions(
        params.getOutputListItemsSearchParams,
        params.pagingParams,
        params.abortSignal
      );
      return {
        response: response,
        pagingData: params.pagingParams,
        updateStateAfterFetch: params.updateStateAfterFetch
      };
    } catch (err) {
      setError(err);
    }
  }
);

export const updateOutput = createAsyncThunk(
  "Consumables/updateOutput",
  async (data: IConsumablesOutputUpdate, { rejectWithValue }) => {
    try {
      return await consumablesService.updateOutput(data);
    } catch (err: any) {
      return rejectWithValue(err.response.status);
    }
  }
);

export const getOutputForPrint = createAsyncThunk(
  "Consumables/getOutputForPrint",
  async (outputId: number) => {
    try {
      const result = await consumablesService.getOutput(
        outputId,
      );
      return result;
    } catch (err) {
      setError(err);
    }
  }
);


interface outputState {
  loading: boolean;
  listItems: IOutputListItem[];
  outputForPrint?: IExemption;
  totalQuantityAmount: number;
  totalPrice: number;
  searchExemptionPopover: IPopoverState;
  error: any;
}

const initialState: outputState = {
  loading: false,
  listItems: [],
  outputForPrint: undefined,
  totalQuantityAmount: 0,
  totalPrice: 0,
  searchExemptionPopover: { isOpen: false },
  error: null,
};

export const outputSlice = createSlice({
  name: "consumables-output",
  initialState: initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setExemptions: (state, action: PayloadAction<IGetExemptionsResponse>) => {
      state.listItems = action.payload.listItems;
      state.totalQuantityAmount = action.payload.totalQuantityAmount;
      state.totalPrice = action.payload.totalPrice;
      state.loading = false;
    },
    setError: (state, action: PayloadAction<any>) => {
      state.error = action.payload;
    },
    setSearchExemptionPopover: (state, action: PayloadAction<IPopoverState>) => {
      state.searchExemptionPopover = action.payload;
    },
    updateOutputTemporarily: (
      state,
      action: PayloadAction<IConsumablesOutputUpdate>
    ) => {
      for (let listItemIndex = 0; listItemIndex < state.listItems.length; listItemIndex++) {
        if (state.listItems[listItemIndex].outputItemId === action.payload.outputItemId) {
          if (action.payload.note !== undefined) {
            state.listItems[listItemIndex].note = action.payload.note;
          }
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getExemptions.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(getExemptions.fulfilled, (state, action) => {
      if (action.payload) {

        if (action.payload.pagingData.fetchMode === 'replace') {
          const elementsFetched = action.payload.response.listItems.length;
          const newListItems = action.payload.response.listItems;
          const newFirstElementId = newListItems?.[0]?.outputItemId;
          const newLastElementId = newListItems?.[newListItems.length - 1]?.outputItemId;
          state.listItems = newListItems;
          action.payload.updateStateAfterFetch({
            fetchSide: "replace",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else if (action.payload.pagingData.beforeId) {
          const elementsFetched = action.payload.response.listItems.length;
          const newInputs = [...action.payload.response.listItems, ...state.listItems];
          const newFirstElementId = newInputs?.[0]?.outputItemId;
          const newLastElementId = newInputs?.[newInputs.length - 1]?.outputItemId;
          state.listItems = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "beforeFirst",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        } else {
          const elementsFetched = action.payload.response.listItems.length;
          const newInputs = [...state.listItems, ...action.payload.response.listItems];
          const newFirstElementId = newInputs?.[0]?.outputItemId;
          const newLastElementId = action.payload.response.listItems?.[elementsFetched - 1]?.outputItemId;
          state.listItems = newInputs;
          action.payload.updateStateAfterFetch({
            fetchSide: "afterLast",
            newFirstElementId,
            newLastElementId,
            elementsFetched
          });
        }

        state.totalQuantityAmount = action.payload.response.totalQuantityAmount;
        state.totalPrice = action.payload.response.totalPrice;
        state.loading = false;
      }
    });
    builder.addCase(getExemptions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.error;
    });
    builder.addCase(updateOutput.pending, (state) => {
    });
    builder.addCase(updateOutput.fulfilled, (state, action) => {
      if (action.payload) {
        action.payload.listItems.forEach(newOutputItem => {
          state.listItems = state.listItems.map(i => {
            if (i.outputItemId === newOutputItem.outputItemId) {
              return newOutputItem;
            } else {
              return i;
            }
          });
        });
      }
    });
    builder.addCase(updateOutput.rejected, (state, action) => {
      state.error = action.error;
    });
    builder.addCase(getOutputForPrint.fulfilled, (state, action) => {
      state.loading = false;
      state.outputForPrint = action.payload ? action.payload : undefined;
    });
  },
});

export const {
  setExemptions,
  setLoading,
  setError,
  setSearchExemptionPopover,
  updateOutputTemporarily,
} = outputSlice.actions;

export const selectState = (state: RootState) => state.consumables.output;
export default outputSlice.reducer;
